import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import { TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface DeletedGate {
  id: number;
  name: string;
  location: string;
  type: string;
  direction: string;
  deletedAt: string;
  deletedBy: string;
  deleteReason?: string;
}

const DatabaseManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'gates' | 'users' | 'seats'>('gates');
  const [deletedGates, setDeletedGates] = useState<DeletedGate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'gates') {
      loadDeletedGates();
    }
  }, [activeTab]);

  const loadDeletedGates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/gates/deleted`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('삭제된 게이트 조회 실패');
      }

      const data = await response.json();
      setDeletedGates(data);
    } catch (error) {
      console.error('삭제된 게이트 로드 실패:', error);
      alert('삭제된 게이트를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (gateId: number) => {
    if (!window.confirm('이 게이트를 복구하시겠습니까?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/gates/${gateId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('게이트 복구 실패');
      }

      alert('게이트가 복구되었습니다.');
      loadDeletedGates();
    } catch (error) {
      console.error('게이트 복구 실패:', error);
      alert('게이트 복구에 실패했습니다.');
    }
  };

  const handlePermanentDelete = async (gateId: number) => {
    const confirmed = window.confirm(
      '⚠️ 경고: 이 게이트를 영구 삭제하시겠습니까?\n\n' +
      '영구 삭제된 데이터는 복구할 수 없습니다.\n' +
      '계속하시려면 "확인"을 클릭하세요.'
    );

    if (!confirmed) {
      return;
    }

    const doubleConfirm = window.confirm(
      '정말로 영구 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다!'
    );

    if (!doubleConfirm) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/gates/${gateId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('게이트 영구 삭제 실패');
      }

      alert('게이트가 영구 삭제되었습니다.');
      loadDeletedGates();
    } catch (error) {
      console.error('게이트 영구 삭제 실패:', error);
      alert('게이트 영구 삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR');
  };

  const getDirectionLabel = (direction: string) => {
    return direction === 'entry' ? '입구' : direction === 'exit' ? '출구' : '양방향';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">DB 관리</h1>
          <p className="mt-2 text-sm text-gray-600">
            삭제된 데이터를 관리하고 복구하거나 영구 삭제할 수 있습니다.
          </p>
        </div>

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('gates')}
                className={`${
                  activeTab === 'gates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                삭제된 게이트
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                disabled
              >
                삭제된 회원
                <span className="ml-2 text-xs text-gray-400">(준비중)</span>
              </button>
              <button
                onClick={() => setActiveTab('seats')}
                className={`${
                  activeTab === 'seats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                disabled
              >
                삭제된 좌석
                <span className="ml-2 text-xs text-gray-400">(준비중)</span>
              </button>
            </nav>
          </div>
        </div>

        {/* 삭제된 게이트 탭 내용 */}
        {activeTab === 'gates' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">삭제된 게이트</h2>
              <p className="mt-1 text-sm text-gray-500">
                삭제된 게이트를 복구하거나 영구 삭제할 수 있습니다.
              </p>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-500">로딩 중...</p>
              </div>
            ) : deletedGates.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">삭제된 게이트가 없습니다.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        게이트명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        위치
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        방향
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        삭제일시
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        삭제자
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletedGates.map((gate) => (
                      <tr key={gate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {gate.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {gate.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {gate.location}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getDirectionLabel(gate.direction)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(gate.deletedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {gate.deletedBy || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleRestore(gate.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-1" />
                            복구
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(gate.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            영구삭제
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 삭제된 회원 탭 (준비중) */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">준비중입니다.</p>
          </div>
        )}

        {/* 삭제된 좌석 탭 (준비중) */}
        {activeTab === 'seats' && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">준비중입니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseManagement;
